import React, { useEffect, useReducer, useRef, useState } from "react";
import { useViewerStore } from "../../../../../../store/store";
import { Badge, Button, Flex, InputNumber, Select, Spin, Tag, Tooltip } from 'antd';
import {
    useProductionPanelLoading,
    useProductionPanelLoadingMessage,
} from "../../../../../../hooks/calculationStateHook";
import { useCurrentUser } from "../../../../../../contexts/UserContext";
import { FaceItem, RoofInformationTableProps, RoofMesh, RoofSquareInfo } from "../../../../../../@types/types";
import { AlertType, CalculationType } from "../../../../../../@types/enums";
import ProductionLoader from "./ProductionLoader";
import { InfoCircleOutlined, InfoOutlined } from "@ant-design/icons";
import PitchOverrideInfoTable from "../../../Modal/Modal-Content/PitchOverrideInfoTable";
import { isGroundMount } from "../../../Three-JS-Viewer/Controllers/PlaneRoofControls";
import Toggle from "../../../../../Pure-UI-Components/Toggle/Toggle";
import { cloneDeep } from "lodash";
import {
    isLightReachFinancePartner,
    syncLightReachSitePitchWarning,
} from "../../../../../../helpers/lightReachWarning";

const RoofInformationTable: React.FC<RoofInformationTableProps> = ({ shortened }) => {
    const trueShortened = shortened ? shortened : window.innerWidth <= 1366;

    const { hasCapability } = useCurrentUser();
    const isPending = useProductionPanelLoading();
    const loadingMessage = useProductionPanelLoadingMessage();
    const [editablePitch, setEditablePitch] = useState<any>({});
    const [currentEditingValue, setCurrentEditingValue] = useState<any>({});
    const pitchDisplayMode = useViewerStore(state => state.pitchDisplayMode);
    const sceneViewer = useViewerStore(state => state.sceneViewer);
    const roofInfoArr = useViewerStore(state => state.roofInfoArr);
    const modeCount33Arr = useViewerStore(state => state.buildingWise33PercentRuleModuleCountList);
    const accessoryBuildingList = useViewerStore(state => state.accessoryBuildingList);
    const isPitchOverrideByUser = useViewerStore(state => state.isPitchOverrideByUser);
    const financePartnerDetails = useViewerStore(state => state.financePartnerDetails);
    const designList = useViewerStore(state => state.designList);
    const roofSquareTableData = useViewerStore(state => state.roofSquareTableData);
    const detachedStructureBuildingIds = useViewerStore(state => state.detachedStructureBuildingIds);
    const [editableRoofNum, setEditableRoofNum] = useState<any>({});
    const [currentRoofNumValue, setCurrentRoofNumValue] = useState<any>({});
    const [isRoofEditMode, setIsRoofEditMode] = useState(false);
    const [showInactiveRoofs, toggleShowInactiveRoofs] = useReducer(state => !state, false);
    const [roofPitchData, setRoofPitchData] = React.useState<Array<{
        key: number;
        roofId: number;
        originalPitch: number;
        overridePitch: number;
    }>>([])

    const inputRefs: any = useRef({});
    const setRef = (roofFaceId: string, el: any) => {
        inputRefs.current[roofFaceId] = el;
    };

    const hasBallastedModules = sceneViewer?.moduleMeshArr?.some((m: any) => m.userData?.isBallastedRackType) ?? false;

    const handleRoofDoubleClick = (roofFaceId: string) => {

        if (!hasCapability('Override_RoofLabel')) {
            sceneViewer.viewerAlert({
                show: true,
                title: "Error",
                message: "You don't have permission to override Roof Label",
                isCloseAfterDisplay: true,
                messageType: AlertType.error
            });
            return;
        }
        // Enable roof label edit mode
        setIsRoofEditMode(true);
        setTimeout(()=> inputRefs.current?.[roofFaceId]?.focus(), 400);
    };

    const onSaveRoofLabels = () => {
        // //  current labels for all rows
        // const allRoofs = roofInfoArr.filter(
        //     (i: any) => !accessoryRoofs.includes(i.roofFaceId) && Number(i.arraySize) > 0
        // );

        const allFinalLabels = roofInfoArr.map((roof: any) => {
            const pending = currentRoofNumValue[roof.roofFaceId];
            return {
                roofFaceId: roof.roofFaceId,
                label: pending !== undefined ? Math.round(pending) : roof.roofNum,
            };
        });

        // Validation - check 0 as label
        const anyZero = allFinalLabels.some(r => r.label <= 0);
        if (anyZero) {
            sceneViewer.viewerAlert({
                show: true,
                title: "Error",
                message: `Roof label cannot be zero, negative or empty`,
                isCloseAfterDisplay: true,
                messageType: AlertType.error,
            });
            return;
        }

        // validation – check duplicates in newLabel
        const seen = new Set<number>();
        const duplicates: number[] = [];

        for (const r of allFinalLabels) {
            if (seen.has(r.label)) {
                duplicates.push(r.label);
            } else {
                seen.add(r.label);
            }
        }

        if (duplicates.length > 0) {
            sceneViewer.viewerAlert({
                show: true,
                title: "Error",
                message: `Same Roof Label cannot be used for multiple roofs. \n Duplicate Roof Labels: ${[
                    ...new Set(duplicates),
                ].join(", ")}`,
                isCloseAfterDisplay: true,
                messageType: AlertType.error,
            });
            return;
        }

        const visibleRoofs = roofInfoArr.filter(
            (i: any) => (showInactiveRoofs || (!accessoryRoofs.includes(i.roofFaceId) && Number(i.arraySize) > 0))
        );

        const labelsByRoofFaceId = visibleRoofs.map(roof => {
            const pending = currentRoofNumValue[roof.roofFaceId];
            return {
                roofFaceId: roof.roofFaceId,
                roofNum: roof.roofNum,
                oldLabel: roof.nameOverride ?? roof.roofNum,
                buildingName: roof.buildingName,
                newLabel: pending !== undefined ? Math.round(pending) : roof.roofNum,
            };
        });

        // showing an alert only if something actually changed
        const changedRoofs = labelsByRoofFaceId.filter((r) => r.oldLabel !== r.newLabel);

        if (changedRoofs.length === 0) {
            sceneViewer.viewerAlert({
                show: true,
                title: "Info",
                message: "No roof labels were changed.",
                messageType: AlertType.info,
            });
            return;
        }

        sceneViewer.viewerAlert({
            show: true,
            isModal: true,
            title: "Confirm Roof Label Changes",
            message: (
                <>
                    <p>Are you sure you want to update the following roof labels?<br /> The changes will be synced only after design finalization.
                        <br />To continue, click "Confirm". To undo your changes, click "Cancel."</p>
                    <br />
                    <ul>
                        {changedRoofs.map((r) => (
                            <li key={r.roofFaceId}>
                                Original Roof Label: {r.oldLabel} → New Roof Label: {r.newLabel}
                            </li>
                        ))}
                    </ul>
                    <br />
                    <div className={"modal-bottom-action-button-container"}>
                        <Button
                            className={"editor__control--finalize modal-confirm-button"}
                            type="primary"
                            onClick={() => {
                                sceneViewer.viewerAlert({ show: false });
                                updateRoofLabelChanges(labelsByRoofFaceId);
                            }}
                        >
                            Confirm
                        </Button>
                        <Button
                            className="editor__control--finalize modal-cancel-button"
                            onClick={() => {
                                sceneViewer.viewerAlert({ show: false });
                                // Reset ALL edited roof numbers back to their original values from roofInfoArr
                                const allRoofs = roofInfoArr.filter(
                                    (i: any) => (showInactiveRoofs || (!accessoryRoofs.includes(i.roofFaceId) && Number(i.arraySize) > 0))
                                );

                                allRoofs.forEach((roof: any) => {
                                    setCurrentRoofNumValue((prev: any) => ({
                                        ...prev,
                                        [roof.roofFaceId]: roof.nameOverride ?? roof.roofNum,
                                    }));
                                });
                                setEditableRoofNum({});
                                setIsRoofEditMode(false);
                            }}
                        >
                            Cancel
                        </Button>
                    </div>
                </>
            ),
            messageType: AlertType.info,
        });
    };

    const cancelRoofLabelEditMode = () => {
        setEditableRoofNum({});
        setCurrentRoofNumValue({});
        setIsRoofEditMode(false);
    }

    const updateRoofLabelChanges = (
        labelsByRoofFaceId: Array<{
            roofFaceId: string;
            oldLabel: number;
            newLabel: number;
            buildingName: number;
            roofNum: number;
        }>
    ) => {
        const updatedRoofInfoArr = roofInfoArr.map((roof: any) => {
            const match = labelsByRoofFaceId.find((r) => r.roofFaceId === roof.roofFaceId);
            return match ? { ...roof, nameOverride: match.newLabel } : roof;
        });

        const updatedRoofSquareData = cloneDeep(roofSquareTableData);

        // Update roofname everywhere
        labelsByRoofFaceId.forEach((item) => {
            // Update mesh userData
            const meshUserData = sceneViewer.roofMeshArr.find(
                (roof: RoofMesh) => roof.roofFaceId === item.roofFaceId
            );
            if (meshUserData) {
                meshUserData.userData.nameOverride = item.newLabel;
            }

            // Update face data
            const face = sceneViewer.roofs[0]?.faces?.find((face: FaceItem) => face.roofFaceId === item.roofFaceId);
            if (face) face.nameOverride = item.newLabel;

            // Update roof square data
            const building = updatedRoofSquareData.find(data => data.buildingName === String(item.buildingName));
            const roofSquareData = building ? building.data.find(roof => roof.roofId === item.roofNum) : undefined;
            if (roofSquareData) roofSquareData.nameOverride = item.newLabel;
        });

        useViewerStore.setState({ 
            roofInfoArr: updatedRoofInfoArr,
            roofSquareTableData: updatedRoofSquareData,
            isRoofLabelOverrideByUser: true 
        });

        // Refresh roof tags if they are visible
        if (sceneViewer.roofTagGroup.visible) {
            sceneViewer.toggleRoofTag(true);
        }

        // Clear edit buffer
        setEditableRoofNum({});
        setCurrentRoofNumValue({});
        setIsRoofEditMode(false);
        sceneViewer.viewerAlert({
            show: true,
            title: "Success",
            message: "Roof labels updated successfully.",
            messageType: AlertType.success,
        });
    };

    useEffect(() => {
        if (isRoofEditMode) {
            const allRoofs = roofInfoArr.filter(
                (i: any) => (showInactiveRoofs || (!accessoryRoofs.includes(i.roofFaceId) && Number(i.arraySize) > 0))
            );
            const editableRoofs: any = {};
            const roofValue: any = {};

            allRoofs.forEach((roof: any) => {
                editableRoofs[roof.roofFaceId] = true;
                roofValue[roof.roofFaceId] =
                    currentRoofNumValue[roof.roofFaceId] ?? roof.nameOverride;
            });

            setEditableRoofNum(editableRoofs);
            setCurrentRoofNumValue(roofValue);
        }
    }, [roofInfoArr, showInactiveRoofs, isRoofEditMode]);

    const thArr = [
        { key: 'roofNum', label: 'Roof ID', des: 'Roof' }, // Roof column
        { key: 'azimuth', label: 'Azimuth', des: 'Azimuth' },
        ...(hasBallastedModules ? [
            { key: 'ballastPitch', label: 'Ballast Pitch', des: 'Ballast Pitch' },
            { key: 'ballastAzimuth', label: 'Ballast Azimuth', des: 'Ballast Azimuth' },
        ] : []),
        { key: 'solarAccess', label: 'Solar Access', des: 'Solar Access' },
        { key: 'efficiency', label: 'Efficiency', des: 'Efficiency' },
        { key: 'modules', label: 'Modules', des: 'Modules' },
        { key: 'arraySize', label: 'Array Size', des: 'Array Size' },
        ...(hasCapability('Monthly_Estimate') && !trueShortened) ? [
            { key: 'utility_production', label: 'Utility Production', des: 'Utility Production' },
            { key: 'actual_production', label: 'Actual Production', des: 'Actual Production' }
        ] : []
    ];

    const accessoryRoofs: any[] = [];

    if (sceneViewer) {
        for (const roof of sceneViewer.roofMeshArr) {
            if (accessoryBuildingList.includes(roof.userData.buildingName)) {
                accessoryRoofs.push(roof.roofFaceId);
            }
        }
    }

    const roofSplitIntoBuilding = roofInfoArr.reduce((acc: any, roof: any) => {
        const buildingName = roof.buildingName;
        if (!acc[buildingName]) {
            acc[buildingName] = [];
        }
        acc[buildingName].push(roof);
        acc[buildingName].modeCount33 = modeCount33Arr?.find((b) => b.name === buildingName)?.modeCount33;
        return acc;
    }, {});


    const roofSplitIntoBuildingArr = Object.keys(roofSplitIntoBuilding).map((buildingName) => roofSplitIntoBuilding[buildingName]);

    const handlePitchDoubleClick = (roofFaceId: string, currentPitch: number) => {
        if (!hasCapability('Override_Table')) {
            sceneViewer.viewerAlert({
                show: true,
                title: "Error",
                message: "You don't have permission to override pitch",
                isCloseAfterDisplay: true,
                messageType: AlertType.error
            });
            return;
        }

        if (designList.length === 0) {
            sceneViewer.viewerAlert({
                show: true,
                title: "Error",
                message: "Cannot override pitch without saving at least one design/version",
                isCloseAfterDisplay: true,
                messageType: AlertType.error
            });
            return;
        }

        if (pitchDisplayMode !== 'Pitch Override') {
            sceneViewer.viewerAlert({
                show: true,
                title: "Info",
                message: "Cannot Edit Original Pitch , Select Adjusted from Pitch Dropdown to override pitch",
                isCloseAfterDisplay: true,
                messageType: AlertType.error
            });
            return;
        }
        if (isRoofEditMode) cancelRoofLabelEditMode();
        if (!editablePitch[roofFaceId]) {
            setEditablePitch((prev: any) => ({
                ...prev,
                [roofFaceId]: true
            }));
            setCurrentEditingValue((prev: any) => ({
                ...prev,
                [roofFaceId]: currentPitch
            }));
        }
    };

    const handlePitchChange = (value: number, roofFaceId: string) => {
        setCurrentEditingValue((prev: any) => ({
            ...prev,
            [roofFaceId]: value
        }));
    };

    const updatePitch = (roofFaceId: string, newPitch: number, roofTagId: number) => {
        const roundedNewPitch = Math.round(newPitch);

        const updatedRoofInfoArr = roofInfoArr.map((roof) =>
            roof.roofFaceId === roofFaceId ? { ...roof, pitchOverride: roundedNewPitch } : roof
        );
        const roofInMeshArr = sceneViewer.roofMeshArr.find((roof: RoofMesh) => roof.roofFaceId === roofFaceId);
        if (roofInMeshArr) {
            roofInMeshArr.userData.pitchOverride = roundedNewPitch;
        }
        sceneViewer.updatePitchOverrideText(roofTagId, roundedNewPitch);

        useViewerStore.setState({ roofInfoArr: updatedRoofInfoArr, isPitchOverrideByUser: true });

        if (isLightReachFinancePartner(financePartnerDetails?.name)) {
            syncLightReachSitePitchWarning(sceneViewer);
        }
    };

    const handleBlur = (roofFaceId: string, originalPitch: number, roofTagId: number, curentValue: number) => {
        const newPitch = Math.round(currentEditingValue[roofFaceId]);

        // if (newPitch === Math.round(originalPitch)) {
        //     // No changes, so no need to show alert
        //     setEditablePitch((prev) => ({
        //         ...prev,
        //         [roofFaceId]: false
        //     }));
        //     return;
        // }

        const minValue = Math.round(originalPitch) - 5;
        const maxValue = Math.round(originalPitch) + 5;

        if (newPitch < minValue || newPitch > maxValue) {
            sceneViewer.viewerAlert({
                show: true,
                title: 'Limit Exceeded',
                message: (
                    <>
                        <p>Error: Cannot set pitch to {newPitch}.</p>
                        <br />
                        <p>Pitch can be adjusted only by ± 5 degrees, Pitch value should be
                            between {minValue} and {maxValue}</p>
                        <br />
                        <p>Original Pitch: {Math.round(originalPitch)}</p>
                    </>
                ),
                messageType: AlertType.error,
                autoCloseSeconds: 8000,
                isCloseAfterDisplay: true,
            })
            setCurrentEditingValue((prev: any) => ({
                ...prev,
                [roofFaceId]: originalPitch
            }));
            setEditablePitch((prev: any) => ({
                ...prev,
                [roofFaceId]: false
            }));


            return;
        }

        sceneViewer.viewerAlert({
            show: true,
            title: 'Roof Pitch Override',
            message: (
                <>
                    <p>Are you sure you want to overwrite the current pitch value for roof {roofTagId}? To continue,
                        click "Confirm". To undo your changes, click "Cancel."</p>
                    <br />
                    <p>Original Pitch: {Math.round(originalPitch)} → Previous Adjusted
                        Pitch: {Math.round(curentValue)} → New Pitch: {newPitch}</p>
                    <br />
                    <div className={"modal-bottom-action-button-container"}>
                        <Button
                            className={"editor__control--finalize modal-confirm-button"}
                            type="primary"
                            onClick={() => {
                                sceneViewer.viewerAlert({ show: false });
                                updatePitch(roofFaceId, newPitch, roofTagId);
                            }}
                        >
                            Confirm
                        </Button>
                        <Button
                            className="editor__control--finalize modal-cancel-button"
                            onClick={() => {
                                sceneViewer.viewerAlert({ show: false });
                                setCurrentEditingValue((prev: any) => ({
                                    ...prev,
                                    [roofFaceId]: originalPitch
                                }));
                            }}
                        >
                            Cancel
                        </Button>
                    </div>
                </>
            ),
            messageType: AlertType.info,
            isModal: true,
        });

        setEditablePitch((prev: any) => ({
            ...prev,
            [roofFaceId]: false
        }));
    };

    const onClickRoofPitchOverrideInfo = () => {
        const newData = sceneViewer.roofMeshArr
            .filter((roofMesh: RoofMesh) => {
                const originalPitch = roofMesh.oriAng.tilt;
                const overriddenPitch = roofMesh.userData.pitchOverride;
                const roofId = roofMesh.userData.roof_tag_id;

                return overriddenPitch !== undefined &&
                    overriddenPitch !== originalPitch &&
                    !roofPitchData.some(roof => roof.roofId === roofId);
            })
            .map((roofMesh: RoofMesh) => ({
                key: roofMesh.userData.roof_tag_id,
                roofId: roofMesh.userData.nameOverride ??  roofMesh.userData.roof_tag_id,
                originalPitch: roofMesh.oriAng.tilt,
                overridePitch: roofMesh.userData.pitchOverride,
            }));

        const updatedData = [...roofPitchData, ...newData];

        if (newData.length > 0) {
            setRoofPitchData(updatedData);
        }

        sceneViewer.viewerAlert({
            show: true,
            isModal: true,
            title: "Pitch Override Info",
            message: <PitchOverrideInfoTable roofPitchData={updatedData} />,
            messageType: AlertType.info
        });
    };


    return (
        <div className={"tab-content-container"}>
            <div className={"tab-content-scrollable-container"}>
                {isPending ? (
                    <ProductionLoader isPopoutVersion={!shortened} message={loadingMessage} />
                ) : (
                    <>
                        {
                            isPitchOverrideByUser && (
                                <Flex
                                    gap={10}
                                >
                                    <Badge status="success" text={"Pitch is adjusted"} />
                                    <Tooltip title="=Click to show overriden roof pitch data" placement={"right"}>
                                        <InfoCircleOutlined onClick={onClickRoofPitchOverrideInfo} />
                                    </Tooltip>

                                    <span></span>
                                </Flex>


                            )
                        }
                        <div className="tab__flex tab__flex--rightToggle" style={{ margin: '10px' }}>
                            <span className="tab__column--subtitle" style={{ marginTop: "0", marginBottom: "0" }}>
                                Show Inactive Roofs
                            </span>
                            <Toggle
                                on={showInactiveRoofs}
                                onChange={toggleShowInactiveRoofs}
                            />
                        </div>
                        <table className={"tab__table"}>
                            <thead>
                                <tr>
                                    {/* Roof Column */}
                                    <th key="roofNum">{
                                        trueShortened ? 'R' : 'Roof'

                                    }</th>
                                    {/* Pitch Column */}
                                    <th key="pitch">

                                        <span
                                            className={"pitchOverrideLabel"}
                                        >
                                            {trueShortened ? 'P' : 'Pitch'}
                                        </span>
                                        <Select
                                            className={`pitchOverrideSelect ${trueShortened ? 'adjustPitchColumnWidth' : ''}`}
                                            value={pitchDisplayMode}
                                            onChange={(value) => useViewerStore.setState({ pitchDisplayMode: value })}
                                            options={[
                                                { value: 'Pitch', label: 'Original' },
                                                { value: 'Pitch Override', label: 'Adjusted' },
                                            ]}
                                        />
                                    </th>
                                    {thArr.filter((item) => item.key !== 'roofNum').map((item) => (
                                        <th key={item.des}>{trueShortened ? String(item.des)[0] : item.des}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {roofSplitIntoBuildingArr.map((buildingWiseArr, idx) => (
                                    buildingWiseArr
                                        .filter((i: any) => (showInactiveRoofs || (!accessoryRoofs.includes(i.roofFaceId) && Number(i.arraySize) > 0)))
                                        .map((data: any, index: number) => (
                                            <React.Fragment key={`${idx}-${data.roofFaceId}`}>
                                                {index === 0 && (
                                                    <tr key={`building-${idx}`} className="building-item">
                                                        <td colSpan={thArr.length + 1} style={{ textAlign: "left" }}>
                                                            <label>Building {data.buildingName}
                                                                &nbsp;
                                                                {
                                                                    isGroundMount(sceneViewer.roofMeshArr.find((roof) => roof.roofFaceId === data.roofFaceId)!) ? (
                                                                        <Tag color="blue">Ground Mount</Tag>
                                                                    )
                                                                        :
                                                                        <>
                                                                            - 33% Modules: {(isNaN(buildingWiseArr.modeCount33) || buildingWiseArr.modeCount33 === undefined) ? <Tag color="red">Missing Sq_footage value in Aurora</Tag> : buildingWiseArr.modeCount33}
                                                                        </>
                                                                }

                                                            </label> &nbsp;

                                                            {
                                                                detachedStructureBuildingIds.includes(Number(data.buildingName)) && (
                                                                    <Tag color="blue">Detached Structure</Tag>)
                                                            }

                                                        </td>
                                                    </tr>
                                                )}
                                                <tr key={data.roofFaceId} data-roofid={data.roofFaceId}>
                                                    <td key="roofNum" align="center"
                                                        onDoubleClick={()=> handleRoofDoubleClick(data.roofFaceId)}
                                                    >  {editableRoofNum[data.roofFaceId] ? (
                                                        <InputNumber
                                                            value={Math.round(currentRoofNumValue[data.roofFaceId] ?? data.roofNum)}
                                                            onChange={(value) => { if (value) setCurrentRoofNumValue((prev: any) => ({ ...prev, [data.roofFaceId]: value })) }}
                                                            ref={(el) => setRef(data.roofFaceId, el)}
                                                            onPressEnter={() => setEditableRoofNum((prev: any) => ({ ...prev, [data.roofFaceId]: true }))}
                                                            onKeyDown={(e) => { if (e.key === "Escape") cancelRoofLabelEditMode() }}
                                                        />

                                                    ) : (
                                                        <span>{Math.round(currentRoofNumValue[data.roofFaceId] ?? data.nameOverride ?? data.roofNum)}</span>
                                                    )}
                                                    </td>
                                                    <td
                                                        key="pitch"
                                                        align="center"
                                                        onDoubleClick={() => handlePitchDoubleClick(data.roofFaceId, data.pitchOverride || data.pitch)}
                                                    >
                                                        {editablePitch[data.roofFaceId] ? (
                                                            <InputNumber
                                                                value={Math.round(currentEditingValue[data.roofFaceId])}
                                                                onChange={(value) => handlePitchChange(value as number, data.roofFaceId)}
                                                                onBlur={(e) => handleBlur(data.roofFaceId, data.pitch, data.roofNum, data.pitchOverride)}
                                                                autoFocus
                                                                onPressEnter={(e) => handleBlur(data.roofFaceId, data.pitch, data.roofNum, data.pitchOverride)}
                                                                onKeyDown={(e) => {
                                                                    //Esc key
                                                                    if (e.key === 'Escape') {
                                                                        setEditablePitch((prev: any) => ({
                                                                            ...prev,
                                                                            [data.roofFaceId]: false
                                                                        }));
                                                                    }
                                                                }}
                                                            />
                                                        ) : (
                                                            <span>
                                                                {Math.round(pitchDisplayMode === 'Pitch Override' ? (data.pitchOverride || data.pitch) : data.pitch)}
                                                            </span>
                                                        )}
                                                    </td>
                                                    {thArr.filter((item) => item.key !== 'roofNum').map((th, thIdx) => {
                                                        let cellValue: any = '-';
                                                        if (th.key === 'arraySize') {
                                                            cellValue = data[th.key];
                                                        } else {
                                                            cellValue = data[th.key] !== undefined && data[th.key] !== null ? Math.round(data[th.key]) : '-';
                                                        }
 
                                                        return (
                                                            <td key={`${data.roofFaceId}-${th.key}`} align="center">
                                                                {cellValue}
                                                            </td>
                                                        );
                                                    })}
                                                </tr>
                                            </React.Fragment>
                                        ))
                                ))}
                            </tbody>
                        </table>
                    </>
                )}
                {isRoofEditMode && (
                    <Flex gap={10} style={{ marginBottom: 10, marginTop: 10 }}> { /* justify-content: 'flex-end' */}
                        <Button type="primary"
                            onClick={onSaveRoofLabels}
                        >
                            Update Roof Labels
                        </Button>
                        <Button onClick={cancelRoofLabelEditMode}
                        >
                            Cancel
                        </Button>
                    </Flex>
                )}
            </div>
        </div>
    );
};

export default RoofInformationTable;
